import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'react-hot-toast';
import { Loader2, Package, CheckCircle2, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaterialTypeSelector = ({ onMaterialTypeSelected }) => {
  const [materialTypes, setMaterialTypes] = useState([]);
  const [selectedMaterialType, setSelectedMaterialType] = useState(null);
  const [numberRange, setNumberRange] = useState(null);
  const [materialNumber, setMaterialNumber] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMaterialTypes();
  }, []);

  const loadMaterialTypes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/material-types`);
      setMaterialTypes(response.data || []);
    } catch (error) {
      console.error('Error loading material types:', error);
      toast.error('Failed to load material types');
    } finally {
      setLoading(false);
    }
  };

  const previewMaterialNumber = async (numberRangeId) => {
    setIsGenerating(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/number-ranges/${numberRangeId}/peek-next-number`);
      setMaterialNumber(response.data.material_number);
    } catch (error) {
      console.error('Error previewing material number:', error);
      toast.error('Failed to preview material number');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleMaterialTypeSelect = async (materialType) => {
    setSelectedMaterialType(materialType);

    try {
      // Get number range for this material type
      if (materialType.number_range_id) {
        const response = await axios.get(`${BACKEND_URL}/api/number-ranges/${materialType.number_range_id}`);
        setNumberRange(response.data);

        // If internal range, preview the next number without consuming it
        if (response.data.range_type === 'INTERNAL') {
          await previewMaterialNumber(materialType.number_range_id);
        } else {
          setMaterialNumber('');
        }

        // Auto-scroll to number range details card after a brief delay
        setTimeout(() => {
          const numberRangeCard = document.querySelector('[class*="border-orange-200"]');
          if (numberRangeCard) {
            numberRangeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      } else {
        setNumberRange(null);
        setMaterialNumber('');
        toast.error('No number range assigned to this material type');
      }
    } catch (error) {
      console.error('Error loading number range:', error);
      toast.error('Failed to load number range');
    }
  };

  const handleContinue = () => {
    if (!selectedMaterialType) {
      toast.error('Please select a material type');
      return;
    }

    if (!materialNumber) {
      toast.error('Material number is required');
      return;
    }
    
    // For external ranges, validate that material number starts with the required 3-character prefix
    if (numberRange && numberRange.range_type === 'EXTERNAL') {
      const prefix = numberRange.prefix;
      if (!prefix || prefix.length !== 3) {
        toast.error('External range configuration error: Prefix must be exactly 3 characters');
        return;
      }
      if (!materialNumber.startsWith(prefix)) {
        toast.error(`Material number must start with prefix '${prefix}'`);
        return;
      }
      if (materialNumber.length < 4) {
        toast.error('Material number must be at least 4 characters (3-character prefix + identifier)');
        return;
      }
    }

    onMaterialTypeSelected({
      materialType: selectedMaterialType,
      materialNumber: materialNumber,
      numberRange: numberRange
    });
  };

  if (loading) {
    return (
      <Card className="p-8 shadow-sm">
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
          <p className="text-sm text-gray-600">Loading material types...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-8 shadow-sm">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Select Material Type</h2>
          <p className="text-sm text-gray-600">
            Choose the material type that best describes your product. The number range will be automatically assigned.
          </p>
        </div>

        {/* Material Type Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {materialTypes.map((mt) => {
            const isSelected = selectedMaterialType?.material_type_id === mt.material_type_id;
            const enabledViewsCount = Object.values(mt.enabled_views || {}).filter(v => v).length;
            
            return (
              <button
                key={mt.material_type_id}
                onClick={() => handleMaterialTypeSelect(mt)}
                className={`
                  relative text-left p-5 rounded-lg border-2 transition-all duration-200
                  ${isSelected 
                    ? 'border-orange-500 bg-orange-50 shadow-md ring-2 ring-orange-200' 
                    : 'border-gray-200 hover:border-orange-300 hover:shadow-md bg-white'
                  }
                `}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-3 right-3">
                    <CheckCircle2 className="w-6 h-6 text-orange-600" />
                  </div>
                )}

                {/* Material Type Icon */}
                <div className={`
                  w-12 h-12 rounded-lg flex items-center justify-center mb-3
                  ${isSelected ? 'bg-orange-600' : 'bg-gray-100'}
                `}>
                  <Package className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-600'}`} />
                </div>

                {/* Material Type Code */}
                <div className="mb-2">
                  <h3 className={`text-lg font-bold ${
                    isSelected ? 'text-orange-900' : 'text-gray-900'
                  }`}>
                    {mt.material_type_id}
                  </h3>
                </div>

                {/* Description */}
                <p className={`text-sm mb-3 line-clamp-2 ${
                  isSelected ? 'text-orange-800' : 'text-gray-600'
                }`}>
                  {mt.description}
                </p>

                {/* Number Range Badge */}
                {mt.number_range_id && (
                  <div className="flex items-center gap-2">
                    <span className={`
                      px-2 py-1 text-xs font-medium rounded
                      ${isSelected 
                        ? 'bg-orange-100 text-orange-700' 
                        : 'bg-gray-100 text-gray-600'
                      }
                    `}>
                      Range: {mt.number_range_id}
                    </span>
                    <span className={`
                      px-2 py-1 text-xs font-medium rounded
                      ${isSelected 
                        ? 'bg-orange-100 text-orange-700' 
                        : 'bg-gray-100 text-gray-600'
                      }
                    `}>
                      {enabledViewsCount} views
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Number Range Information - Shows when type is selected */}
      {selectedMaterialType && numberRange && (
        <Card className="p-6 shadow-sm border-orange-200 bg-gradient-to-br from-orange-50 to-white">
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3">Number Range Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Range ID */}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Range ID</label>
                  <p className="text-sm font-semibold text-gray-900 mt-1">{numberRange.number_range_id}</p>
                </div>

                {/* Type */}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</label>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    <span className={`
                      px-2 py-1 rounded text-xs
                      ${numberRange.range_type === 'INTERNAL' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-orange-100 text-orange-700'
                      }
                    `}>
                      {numberRange.range_type}
                    </span>
                  </p>
                </div>

                {/* Range */}
                {numberRange.range_type === 'INTERNAL' && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Range</label>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {numberRange.from_number} - {numberRange.to_number}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Material Number Preview */}
            <div className="pt-4 border-t border-orange-200">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">
                {numberRange.range_type === 'INTERNAL' ? 'Material Number (Preview)' : 'Material Number *'}
              </label>
              
              <div className="relative">
                {numberRange.range_type === 'EXTERNAL' && numberRange.prefix && (
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                    <span className="text-lg font-mono font-bold text-orange-700 bg-orange-100 px-2 py-1 rounded">
                      {numberRange.prefix}
                    </span>
                  </div>
                )}
                <input
                  type="text"
                  className={`w-full px-4 py-3 border-2 border-orange-300 rounded-lg bg-white text-lg font-mono font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    numberRange.range_type === 'EXTERNAL' && numberRange.prefix ? 'pl-20' : ''
                  }`}
                  value={materialNumber}
                  onChange={(e) => setMaterialNumber(e.target.value.toUpperCase())}
                  placeholder={numberRange.range_type === 'INTERNAL' ? 'Generating...' : `${numberRange.prefix || ''}001`}
                  disabled={numberRange.range_type === 'INTERNAL'}
                  maxLength={18}
                />
                {isGenerating && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                  </div>
                )}
              </div>
              
              <p className="text-xs text-gray-600 mt-2">
                {numberRange.range_type === 'INTERNAL' 
                  ? '✨ Preview - Number will be assigned when material is saved' 
                  : `⚠️ Must start with prefix '${numberRange.prefix}' (e.g., ${numberRange.prefix}001, ${numberRange.prefix}ABC)`
                }
              </p>
            </div>

            {/* Continue Button */}
            <div className="flex justify-end pt-2">
              <Button 
                onClick={handleContinue} 
                className="bg-orange-600 hover:bg-orange-700 px-6 py-2.5 flex items-center gap-2"
              >
                Continue to View Selection
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MaterialTypeSelector;